/**
 * Crypto utility for encrypting and decrypting sensitive data using Web Crypto API
 */

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits recommended for AES-GCM

/**
 * Derives an encryption key from a password/seed using PBKDF2
 */
async function deriveKey(password: string, salt: BufferSource): Promise<CryptoKey> {
   const encoder = new TextEncoder();
   const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveBits', 'deriveKey'],
   );

   return crypto.subtle.deriveKey(
      {
         name: 'PBKDF2',
         salt: salt,
         iterations: 100000,
         hash: 'SHA-256',
      },
      keyMaterial,
      { name: ALGORITHM, length: KEY_LENGTH },
      false,
      ['encrypt', 'decrypt'],
   );
}

/**
 * Encrypts data using AES-GCM with a password-derived key
 * @param data - The string data to encrypt
 * @param password - The password/seed to derive the encryption key from
 * @returns Base64-encoded encrypted data with salt and IV prepended
 */
export async function encryptData(data: string, password: string): Promise<string> {
   const encoder = new TextEncoder();

   // Generate random salt and IV
   const salt = crypto.getRandomValues(new Uint8Array(16));
   const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

   // Derive key from password
   const key = await deriveKey(password, salt);

   // Encrypt the data
   const encrypted = await crypto.subtle.encrypt(
      {
         name: ALGORITHM,
         iv,
      },
      key,
      encoder.encode(data),
   );

   // Combine salt + IV + encrypted data
   const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
   combined.set(salt, 0);
   combined.set(iv, salt.length);
   combined.set(new Uint8Array(encrypted), salt.length + iv.length);

   // Return as base64
   return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypts data that was encrypted with encryptData
 * @param encryptedData - Base64-encoded encrypted data
 * @param password - The password/seed used for encryption
 * @returns The decrypted string
 */
export async function decryptData(encryptedData: string, password: string): Promise<string> {
   // Decode from base64
   const combined = Uint8Array.from(atob(encryptedData), (c) => c.charCodeAt(0));

   // Extract salt, IV, and encrypted data
   const salt = combined.slice(0, 16);
   const iv = combined.slice(16, 16 + IV_LENGTH);
   const encrypted = combined.slice(16 + IV_LENGTH);

   // Derive key from password
   const key = await deriveKey(password, salt);

   // Decrypt the data
   const decrypted = await crypto.subtle.decrypt(
      {
         name: ALGORITHM,
         iv,
      },
      key,
      encrypted,
   );

   // Decode and return
   const decoder = new TextDecoder();
   return decoder.decode(decrypted);
}

/**
 * Generates a deterministic encryption seed from user ID
 * This allows credential recovery across sessions
 * Note: For production, consider using a more secure key derivation strategy
 */
export function getUserEncryptionSeed(userId: string): string {
   // In production, you might want to combine this with other factors
   // or use a hardware-backed key if available
   return `quill-studio-v1-${userId}`;
}
